// Currency utility for handling different currencies based on location/settings

// Currency configurations
const CURRENCY_CONFIG = {
  USD: {
    code: 'USD',
    symbol: '$',
    locale: 'en-US',
    name: 'US Dollar'
  },
  GBP: {
    code: 'GBP',
    symbol: '£',
    locale: 'en-GB',
    name: 'British Pound'
  },
  EUR: {
    code: 'EUR',
    symbol: '€',
    locale: 'en-EU',
    name: 'Euro'
  },
  CAD: {
    code: 'CAD',
    symbol: 'C$',
    locale: 'en-CA',
    name: 'Canadian Dollar'
  },
  AUD: {
    code: 'AUD',
    symbol: 'A$',
    locale: 'en-AU',
    name: 'Australian Dollar'
  }
};

// Auto-detect currency based on location/clinic data
const detectCurrencyFromLocation = (locationData) => {
  if (!locationData) return 'USD'; // Default fallback
  
  const { country, location_name, city } = locationData;
  
  // Check country first
  if (country) {
    const countryLower = country.toLowerCase();
    if (countryLower.includes('uk') || countryLower.includes('united kingdom') || countryLower.includes('britain')) {
      return 'GBP';
    }
    if (countryLower.includes('canada')) return 'CAD';
    if (countryLower.includes('australia')) return 'AUD';
    if (countryLower.includes('euro') || countryLower.includes('germany') || countryLower.includes('france')) {
      return 'EUR';
    }
  }
  
  // Check location name for hints
  if (location_name) {
    const locationLower = location_name.toLowerCase();
    if (locationLower.includes('london') || locationLower.includes('manchester') || 
        locationLower.includes('birmingham') || locationLower.includes('edinburgh')) {
      return 'GBP';
    }
  }
  
  // Check city for hints
  if (city) {
    const cityLower = city.toLowerCase();
    if (cityLower.includes('london') || cityLower.includes('manchester') || 
        cityLower.includes('birmingham') || cityLower.includes('edinburgh')) {
      return 'GBP';
    }
  }
  
  return 'USD'; // Default fallback
};

// Get currency from settings or auto-detect
export const getCurrency = (userSettings = null, locationData = null) => {
  // Priority 1: User setting override
  if (userSettings?.currency) {
    return userSettings.currency;
  }
  
  // Priority 2: Stored preference in localStorage
  const storedCurrency = localStorage.getItem('preferred_currency');
  if (storedCurrency && CURRENCY_CONFIG[storedCurrency]) {
    return storedCurrency;
  }
  
  // Priority 3: Auto-detect from location
  return detectCurrencyFromLocation(locationData);
};

// Format currency amount
export const formatCurrency = (amount, currencyCode = null, userSettings = null, locationData = null) => {
  if (!amount && amount !== 0) return '—';
  
  const currency = currencyCode || getCurrency(userSettings, locationData);
  const config = CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.USD;
  
  try {
    return new Intl.NumberFormat(config.locale, {
      style: 'currency',
      currency: config.code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  } catch (error) {
    // Fallback formatting if Intl.NumberFormat fails
    return `${config.symbol}${Number(amount).toFixed(2)}`;
  }
};

// Get currency symbol
export const getCurrencySymbol = (currencyCode = null, userSettings = null, locationData = null) => {
  const currency = currencyCode || getCurrency(userSettings, locationData);
  const config = CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.USD;
  return config.symbol;
};

// Get all available currencies for settings dropdown
export const getAvailableCurrencies = () => {
  return Object.values(CURRENCY_CONFIG);
};

// Set user currency preference
export const setCurrencyPreference = (currencyCode) => {
  if (CURRENCY_CONFIG[currencyCode]) {
    localStorage.setItem('preferred_currency', currencyCode);
    return true;
  }
  return false;
};

const currencyUtils = {
  formatCurrency,
  getCurrency,
  getCurrencySymbol,
  getAvailableCurrencies,
  setCurrencyPreference,
  CURRENCY_CONFIG
};

export default currencyUtils; 